import { type ReactNode } from "react";
import { Eye, EyeOff, MoreHorizontal } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";

import { SURFACE_ICON_BTN_CLS } from "../constants/layout";

const MENU_ROW_CLS =
  "flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] font-medium transition-colors";

export function NotificationOverflowMenu({
  showHideSeen,
  hideSeen,
  onHideSeenChange,
  hasUnread,
  unreadCount,
  onMarkAllRead,
}: {
  showHideSeen: boolean;
  hideSeen: boolean;
  onHideSeenChange: (checked: boolean) => void;
  hasUnread: boolean;
  unreadCount: number;
  onMarkAllRead: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(SURFACE_ICON_BTN_CLS, "pointer-events-auto inline-flex items-center justify-center")}
          aria-label="More updates actions"
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60 overflow-hidden rounded-xl border-border/60 p-0 shadow-xl"
      >
        <div className="p-1.5">
          {showHideSeen ? (
            <>
              <button
                type="button"
                onClick={() => onHideSeenChange(!hideSeen)}
                aria-pressed={hideSeen}
                className={cn(MENU_ROW_CLS, "text-foreground hover:bg-accent/50")}
              >
                <MenuIconWell>
                  <EyeOff className="size-3.5" aria-hidden />
                </MenuIconWell>
                <span className="min-w-0 flex-1 truncate text-left">Hide seen</span>
                <span className="flex w-9 shrink-0 justify-end">
                  <ToggleSwitch checked={hideSeen} />
                </span>
              </button>
              <div className="mx-2 my-1 h-px bg-border/50" role="separator" />
            </>
          ) : null}

          <button
            type="button"
            disabled={!hasUnread}
            onClick={onMarkAllRead}
            className={cn(
              MENU_ROW_CLS,
              hasUnread
                ? "text-foreground hover:bg-accent/50"
                : "cursor-not-allowed text-muted-foreground/50",
            )}
          >
            <MenuIconWell muted={!hasUnread}>
              <Eye className="size-3.5" aria-hidden />
            </MenuIconWell>
            <span className="min-w-0 flex-1 truncate text-left">Mark all seen</span>
            <span className="flex h-5 w-9 shrink-0 items-center justify-end">
              {unreadCount > 0 ? (
                <span
                  className={cn(
                    "inline-flex h-5 min-w-5 items-center justify-center rounded-md px-1.5",
                    "bg-muted text-[11px] font-medium tabular-nums text-foreground",
                  )}
                >
                  {unreadCount}
                </span>
              ) : null}
            </span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuIconWell({
  children,
  muted = false,
}: {
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-md bg-accent",
        muted ? "text-muted-foreground/50" : "text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function ToggleSwitch({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200",
        checked ? "bg-primary" : "bg-muted-foreground/30",
      )}
      aria-hidden
    >
      <span
        className={cn(
          "pointer-events-none absolute top-[3px] size-3.5 rounded-full bg-white shadow-sm transition-[left] duration-200",
          checked ? "left-[18px]" : "left-[3px]",
        )}
      />
    </span>
  );
}
